import { useState } from 'react';
import { useRoomStore } from '../../../stores/roomStore';
import { Button } from '../../common/Button';
import { Input } from '../../common/Input';
import { ROOM_SCENE_LABELS, type PersistentRoom, type RoomSceneId } from '../../../types/room';

interface RoomInfoSectionProps {
  room: PersistentRoom;
}

/**
 * Section for editing room name and image.
 */
export function RoomInfoSection({ room }: RoomInfoSectionProps) {
  const { updateRoom } = useRoomStore();
  const [name, setName] = useState(room.name);
  const [sceneId, setSceneId] = useState<RoomSceneId>(room.sceneId);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateRoom(room.id, { name, sceneId, imageFile: imageFile || undefined });
    } finally {
      setIsSaving(false);
      setImageFile(null);
    }
  };

  const hasChanges = name !== room.name || sceneId !== room.sceneId || imageFile !== null;

  return (
    <section className="card p-6">
      <h2 className="text-lg font-semibold mb-4">Room Info</h2>

      <div className="space-y-4">
        <Input
          label="Room Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter room name"
        />

        <div>
          <label className="text-sm font-medium text-gray-300 block mb-2">
            Theme
          </label>
          <select
            value={sceneId}
            onChange={(e) => setSceneId(e.target.value as RoomSceneId)}
            className="w-full px-3 py-2.5 bg-[#0f0f0f] border border-border rounded-lg text-white focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
            disabled
          >
            <option value="sci_fi_office">{ROOM_SCENE_LABELS.sci_fi_office}</option>
          </select>
          <p className="mt-2 text-xs text-gray-400">More Rooms Coming Soon</p>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-300 block mb-2">
            Room Image
          </label>
          {room.imageUrl && !imageFile && (
            <img
              src={room.imageUrl}
              alt={room.name}
              className="w-full h-32 object-cover rounded-lg mb-3"
            />
          )}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setImageFile(e.target.files?.[0] || null)}
            className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-surface file:text-white file:cursor-pointer hover:file:bg-surface-hover"
          />
        </div>

        {hasChanges && (
          <Button onClick={handleSave} isLoading={isSaving}>
            Save Changes
          </Button>
        )}
      </div>
    </section>
  );
}
